import React from 'react';
import { ExternalLink, ImageIcon, Loader2, Scan, Upload, X } from 'lucide-react';

const AnalysisUploadPanel = ({
  isDragOver,
  onDragOver,
  onDragLeave,
  onDrop,
  onOpenFilePicker,
  fileInputRef,
  onFileChange,
  previewUrls,
  onMarkPreviewError,
  onRemoveFile,
  scraperLink,
  onAnalyze,
  loading,
  selectedFileCount,
}) => {
  return (
    <>
      <div
        className={`max-w-[458px] min-h-[262px] mx-auto flex items-center justify-center border-[1.5px] border-dashed rounded-2xl p-6 sm:p-10 text-center cursor-pointer transition-all shadow-[6px_6px_54px_rgba(0,0,0,0.05)] ${
          isDragOver ? 'border-brand bg-brand/10' : 'border-ink/20 bg-ink/[0.03] hover:border-brand hover:bg-brand/5'
        }`}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={onOpenFilePicker}
      >
        <input
          type="file"
          className="hidden"
          ref={fileInputRef}
          onChange={onFileChange}
          accept=".jpg,.jpeg,.png,.heic,image/jpeg,image/png,image/heic,image/heif"
          multiple
        />

        {previewUrls.length > 0 ? (
          <div className="w-full grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-4 max-w-full">
            {previewUrls.map((preview, index) => (
              <div key={preview.id || `${preview.name}-${index}`} className="relative group aspect-square">
                {preview.isConverting ? (
                  <div className="w-full h-full rounded shadow-sm border border-hairline bg-surface-2 flex flex-col items-center justify-center text-muted p-2">
                    <Loader2 size={22} className="animate-spin" />
                    <span className="mt-2 text-[10px] text-muted text-center leading-tight">
                      Generating preview...
                    </span>
                  </div>
                ) : preview.hasPreviewError ? (
                  <div className="w-full h-full rounded shadow-sm border border-hairline bg-surface-2 flex flex-col items-center justify-center text-muted p-2">
                    <ImageIcon size={22} />
                    <span className="mt-1 text-[10px] font-semibold uppercase">{preview.extension || 'img'}</span>
                    {preview.isHeic && (
                      <span className="mt-1 text-[10px] text-muted text-center leading-tight">
                        HEIC preview unavailable
                      </span>
                    )}
                  </div>
                ) : (
                  <img
                    src={preview.url}
                    alt={`Preview ${index}`}
                    className="w-full h-full object-cover rounded shadow-sm"
                    onError={() => onMarkPreviewError(index)}
                  />
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveFile(index);
                  }}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            <div className="border-2 border-dashed border-hairline rounded flex flex-col items-center justify-center text-muted aspect-square hover:border-brand transition-colors">
              <ImageIcon size={24} />
              <span className="text-[10px] mt-1">Add More</span>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-5">
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-md bg-ink/10 text-muted">
              <Upload size={24} />
            </span>
            <p className="font-heading text-lg text-muted">
              <span className="font-semibold">Click to upload</span> or drag photos here
            </p>
            <p className="text-xs text-muted">• JPG • PNG • HEIC • multiple supported</p>
          </div>
        )}
      </div>

      <div className="mt-4 text-center">
        <a
          href={scraperLink}
          target="_blank"
          rel="noopener noreferrer"
          className="text-muted hover:text-brand text-xs flex items-center justify-center gap-1 transition-colors"
        >
          <ExternalLink size={14} />
          discover more listing from 20+ auction websites, all in one place!
        </a>
      </div>

      <div className="mt-6 flex justify-center">
        <button
          className={`inline-flex items-center justify-center gap-2 min-w-[330px] max-w-full px-12 py-3 rounded-md font-heading font-semibold border transition-colors ${
            selectedFileCount === 0 || loading
              ? 'border-hairline text-muted opacity-60 cursor-not-allowed'
              : 'border-hairline dark:border-white/50 text-ink hover:bg-white/5 shadow-[0_0_25px_rgba(54,159,255,0.3)]'
          }`}
          disabled={selectedFileCount === 0 || loading}
          onClick={onAnalyze}
        >
          {loading ? (
            <>
              <Loader2 size={16} className="animate-spin" /> Scanning...
            </>
          ) : (
            <>
              <Scan size={16} /> Run Scan
            </>
          )}
        </button>
      </div>
    </>
  );
};

export default AnalysisUploadPanel;
